import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";
import { formatCurrency } from "../../dp/utils/format";

import {
  RgbPackaging,
  createRgbPackagingApi,
  deleteRgbPackagingApi,
  getRgbPackagingsApi,
  updateRgbPackagingApi,
} from "../api/rgbApi";
import { useModalChrome } from "../components/useModalChrome";
import { RGB_HOME_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Rgb.css";

type FormState = { codigo: string; nome: string; descricao: string; valorUnitario: string; ordem: string; ativo: boolean };

const EMPTY: FormState = { codigo: "", nome: "", descricao: "", valorUnitario: "", ordem: "0", ativo: true };

function PackagingModal(props: {
  editing: RgbPackaging | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { editing, isOpen, onClose, onSaved } = props;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  useModalChrome(isOpen, onClose, saving);

  useEffect(() => {
    if (!isOpen) return;
    setForm(
      editing
        ? {
            codigo: editing.codigo ?? "",
            nome: editing.nome,
            descricao: editing.descricao ?? "",
            valorUnitario: editing.valorUnitario === null ? "" : String(editing.valorUnitario),
            ordem: String(editing.ordem),
            ativo: editing.ativo,
          }
        : EMPTY,
    );
  }, [isOpen, editing]);

  const valor = form.valorUnitario.trim() === "" ? null : Number(form.valorUnitario.replace(",", "."));
  const problem = !form.nome.trim()
    ? "Informe o nome."
    : valor !== null && (!Number.isFinite(valor) || valor < 0)
      ? "Valor unitário inválido."
      : !Number.isInteger(Number(form.ordem)) || Number(form.ordem) < 0
        ? "Ordem deve ser um inteiro ≥ 0."
        : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (problem) return;
    setSaving(true);
    const payload = {
      codigo: form.codigo.trim().toUpperCase() || null,
      nome: form.nome.trim(),
      descricao: form.descricao.trim() || null,
      valorUnitario: valor === null ? null : Math.round(valor * 100) / 100,
      ordem: Number(form.ordem),
    };
    try {
      if (editing) await updateRgbPackagingApi(editing.id, { ...payload, ativo: form.ativo });
      else await createRgbPackagingApi(payload);
      toast.success(editing ? "Vasilhame atualizado." : "Vasilhame cadastrado.");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rgbModalOverlay" onClick={() => !saving && onClose()}>
      <div
        className="rgbModal"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Editar vasilhame" : "Novo vasilhame"}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="rgbModal__header">
            <h2 className="rgbModal__title">{editing ? "Editar vasilhame" : "Novo vasilhame"}</h2>
            <button type="button" className="rgbModal__close" onClick={onClose} disabled={saving} aria-label="Fechar">
              ×
            </button>
          </div>
          <div className="rgbModal__body">
            <div className="rgbFormGrid">
              <div className="rgbFormGroup rgbFormGrid--wide">
                <label htmlFor="pkNome">Nome</label>
                <input
                  id="pkNome"
                  className="rgbInput"
                  maxLength={80}
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                />
              </div>
              <div className="rgbFormGroup rgbFormGrid--wide">
                <label htmlFor="pkCodigo">Código (o da planilha de contagem, ex.: D000213)</label>
                <input
                  id="pkCodigo"
                  className="rgbInput"
                  maxLength={20}
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                />
              </div>
              <div className="rgbFormGroup rgbFormGrid--wide">
                <label htmlFor="pkDesc">Descrição</label>
                <input
                  id="pkDesc"
                  className="rgbInput"
                  maxLength={200}
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>
              <div className="rgbFormGroup">
                <label htmlFor="pkValor">Valor unitário (R$)</label>
                <input
                  id="pkValor"
                  className="rgbInput"
                  inputMode="decimal"
                  placeholder="sem valor"
                  value={form.valorUnitario}
                  onChange={(e) => setForm({ ...form, valorUnitario: e.target.value })}
                />
                <span className="rgbFormHint">Custo de reposição, usado na perda estimada.</span>
              </div>
              <div className="rgbFormGroup">
                <label htmlFor="pkOrdem">Ordem</label>
                <input
                  id="pkOrdem"
                  className="rgbInput"
                  type="number"
                  min={0}
                  step={1}
                  value={form.ordem}
                  onChange={(e) => setForm({ ...form, ordem: e.target.value })}
                />
              </div>
              {editing ? (
                <label className="rgbFormGroup" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "var(--eco-primary)" }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--eco-ink)" }}>Ativo</span>
                </label>
              ) : null}
            </div>
            {problem ? <span className="rgbFormHint">{problem}</span> : null}
          </div>
          <div className="rgbModal__footer">
            <button type="button" className="rgbBtn rgbBtn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="rgbBtn rgbBtn--primary" disabled={saving || Boolean(problem)}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RgbPackagingsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(RGB_HOME_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [items, setItems] = useState<RgbPackaging[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; editing: RgbPackaging | null }>({ open: false, editing: null });
  const [removing, setRemoving] = useState<RgbPackaging | null>(null);
  const [removingBusy, setRemovingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getRgbPackagingsApi(true));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao carregar o catálogo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const remove = async () => {
    if (!removing) return;
    setRemovingBusy(true);
    try {
      await deleteRgbPackagingApi(removing.id);
      toast.success("Vasilhame excluído.");
      setRemoving(null);
      load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao excluir.");
    } finally {
      setRemovingBusy(false);
    }
  };

  const visible = items.filter((p) => showInactive || p.ativo);

  if (!token) return null;

  return (
    <div className="rgbLayout">
      <main className="rgbMain">
        <div className="rgbContainer">
          <EcoPageHeader
            crumbs={[{ label: "Vasilhame RGB", onClick: () => navigate(RGB_HOME_PAGE()) }, { label: "Vasilhames" }]}
            title="Vasilhames"
            subtitle="Catálogo de garrafas e caixas retornáveis usado nos apontamentos."
            onBack={goBack}
            backLabel="Voltar para o RGB"
            actions={
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setModal({ open: true, editing: null })}>
                <EcoIcon name="plus" size={18} />
                Novo vasilhame
              </button>
            }
          />

          <div className="rgbNotice">
            Vasilhame sem valor unitário não entra no cálculo de perda estimada. Vasilhame com apontamentos não pode ser
            excluído — desative-o: ele some dos formulários, mas continua nos relatórios.
          </div>

          <div className="rgbControls">
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, minHeight: 40 }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ width: 18, height: 18, accentColor: "var(--eco-primary)" }}
              />
              Mostrar inativos
            </label>
          </div>

          <div className="rgbTableWrapper">
            <table className="rgbTable">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th className="is-num">Valor unitário</th>
                  <th className="is-num">Ordem</th>
                  <th>Situação</th>
                  <th style={{ width: 170 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="rgbEmpty">
                      Carregando...
                    </td>
                  </tr>
                ) : visible.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="rgbEmpty">
                      Nenhum vasilhame cadastrado.
                    </td>
                  </tr>
                ) : (
                  visible.map((p) => (
                    <tr key={p.id}>
                      <td>{p.codigo || <span className="rgbMuted">—</span>}</td>
                      <td>
                        <strong>{p.nome}</strong>
                      </td>
                      <td>{p.descricao || <span className="rgbMuted">—</span>}</td>
                      <td className="is-num">{formatCurrency(p.valorUnitario)}</td>
                      <td className="is-num">{p.ordem}</td>
                      <td>
                        <span className={`rgbBadge ${p.ativo ? "rgbBadge--green" : "rgbBadge--gray"}`}>
                          {p.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td>
                        <div className="rgbTable__actions">
                          <button
                            className="rgbBtn rgbBtn--secondary rgbBtn--small"
                            onClick={() => setModal({ open: true, editing: p })}
                          >
                            Editar
                          </button>
                          <button className="rgbBtn rgbBtn--danger rgbBtn--small" onClick={() => setRemoving(p)}>
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <PackagingModal
        isOpen={modal.open}
        editing={modal.editing}
        onClose={() => setModal({ open: false, editing: null })}
        onSaved={load}
      />

      <EcoConfirmDialog
        open={Boolean(removing)}
        title="Excluir vasilhame?"
        message={`"${removing?.nome ?? ""}" sai do catálogo. Vasilhame com apontamentos não pode ser excluído: desative-o.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={removingBusy}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
